import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// Rxjs
import { Observable, OperatorFunction, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';

// Models
import { CustomerRecurrenceType, CustomerType } from './models';
import { CustomResponseDto } from '@core/model/custom-response-dto';
import { StopFlowResponse } from '@core/model/response/stop-flow-response';
import { AuthenticationResponse } from '@core/model/response/authentication-response';

// Services
import { LocalService } from '@home/storage/local.service';

// Environments
import { environment } from '@environments/environment';
import { RecurrentStatesEnumeration, CUSTOMER_TYPE_CONSTANT } from './configs';

@Injectable({
  providedIn: 'root',
})
export class CustomerRecurrenceService {
  private readonly apiGatewayUrl: string;
  private readonly backUIConfiguration: string;

  constructor(private readonly http: HttpClient, private readonly localService: LocalService) {
    this.apiGatewayUrl = environment.apiGatewayUrl;
    this.backUIConfiguration = `back-ui-configuration/api/`;
  }

  public getRecurrence$(token: string): Observable<CustomResponseDto<CustomerRecurrenceType>> {
    return this.getCustomerInformation$(token).pipe(
      concatMap((responseCustomerInformation: CustomerType) => {
        const document = responseCustomerInformation?.document;
        if (!document) {
          throw new Error('Documento de cliente no disponible');
        }
        const response = {
          message: null,
          error: null,
          status: null,
          path: null,
          response:
            responseCustomerInformation?.type === CUSTOMER_TYPE_CONSTANT
              ? RecurrentStatesEnumeration.IS_RECURRING
              : RecurrentStatesEnumeration.IS_NEW,
        };
        return of(response);
      })
    );
  }

  public getCustomerInformation$(token: string): Observable<CustomerType> {
    return this.http.get<CustomerType>(`${this.apiGatewayUrl}${this.backUIConfiguration}customer`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public saveRecurrenceStatus(recurrence?: CustomerRecurrenceType): void {
    this.localService.setJsonValue('recurrence', recurrence);
  }

  public getRecurrenceStatus(): CustomerRecurrenceType {
    return this.localService.getJsonValue('recurrence');
  }

  public combineAuthenticationAndRecurrenceTypeResponses(): OperatorFunction<
    AuthenticationResponse & StopFlowResponse,
    {
      loginResponse: AuthenticationResponse & StopFlowResponse;
      recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>;
    }
  > {
    return concatMap((loginResponse: AuthenticationResponse & StopFlowResponse) => {
      return this.getRecurrence$(loginResponse.accessToken).pipe(
        map((recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>) => {
          return { loginResponse, recurrenceResponse };
        })
      );
    });
  }
}
